<template>
  <UtilWrapperLayout>
    <UtilGrid>
      <div
        v-if="isArrayNotEmpty(availableDropdowns) || hasTextSearch"
        class="storyblok-news__filters is-narrow"
      >
        <div class="storyblok-news__filters-dropdowns">
          <BlockFilterRoute
            :title="dropdownsTitle"
            :dropdowns="availableDropdowns"
            :color="colorSchema"
            :has-text-search="hasTextSearch"
          />
        </div>
      </div>

      <div
        v-if="mappedPinnedItems.length > 0 && !isFiltered"
        class="storyblok-news__pinned-items is-narrow"
      >
        <div
          v-for="article in mappedPinnedItems"
          :key="article._uid"
          class="block-teaser__pinned-items"
        >
          <BlockItemTeaser
            v-if="article.article"
            :item="article"
          />
        </div>
      </div>

      <div class="storyblok-news is-narrow">
        <div
          v-if="news?.length === 0 && newsStatus === 'success'"
          class="block-news__noArticles"
        >
          <span>{{ getStoryblokTranslation('general', 'no_articles') }}</span>
        </div>

        <div
          class="block-news-wrapper"
          :class="{ 'is-filtered': isFiltered }"
        >
          <UtilRouteLink
            v-for="(item, index) in news"
            :key="item.uuid"
            :link="item"
            :class="{ 'is-first': index === 0 }"
            class="block-news__teaser block-news--mini"
          >
            <div class="block-news__imgWrapper">
              <AtomImage
                v-if="item.content?.image_video?.filename"
                class="block-news__img"
                :image="item.content.image_video"
                behaviour="cover"
                provider="storyblok"
                :height="index === 0 && isFiltered && clientDeviceStore.isGreaterThanTablet ? '410px' : null"
                :width="index === 0 && isFiltered && clientDeviceStore.isGreaterThanTablet ? '420px' : null"
              >
                <div
                  v-if="item.tagColors.backgroundColor"
                  class="block-news__tag"
                  :style="`background-color: ${item.tagColors.backgroundColor}; color: ${item.tagColors.color}`"
                >
                  <span>{{ item.teaserTag }}</span>
                </div>
                <div
                  v-if="item.content.activate_video_teaser"
                  class="block-news__video-tag"
                >
                  <IonIcon
                    class="block_teaser_video__tag-icon"
                    icon-name="video"
                  />
                  <span>{{ item.content.teaser_video }}</span>
                </div>
              </AtomImage>
            </div>

            <div class="block-news__wrapper">
              <div
                v-if="item && item.content.department"
                class="block-news__date"
                :style="`color: ${item.dateColor}`"
              >
                {{ dayjs(item.first_published_at).locale(currentLocale).format('DD. MMMM YYYY') }}
                <span> | </span>
                {{ item.content.department.translated_slugs?.find(
                  (slug) => slug.lang === currentLocale,
                )?.name || item.content.department.name }}
              </div>

              <div class="block-news__title">
                {{ trimString(item.content.title, 35) }}
              </div>

              <div class="block-news__title-large">
                {{ trimString(item.content.title, 400) }}
              </div>

              <div class="block-news__teaser-large">
                <div v-if="!isFiltered || index === 0" class="block-news__lead">
                  <p>
                    <span
                      v-if="item.content.type"
                      class="block-news_article"
                    >{{ item.content.type.name }} |</span>
                    {{ trimString(item.content.lead, 150) }}
                  </p>
                </div>
              </div>

              <ProviderColorSchema :color-schema="item.colorSchema">
                <IonIcon
                  class="block-news__icon"
                  icon-name="chevron-right"
                />
              </ProviderColorSchema>
            </div>
          </UtilRouteLink>
        </div>
      </div>

      <div class="storyblok-news__load-more is-narrow">
        <AtomButton
          v-if="loadMoreVisible"
          class="block-news__more-button"
          :text="getStoryblokTranslation('general', 'more_articles')"
          :color-schema="isInternational ? 'coral' : 'petrol'"
          @click="loadMore()"
        />
      </div>
    </UtilGrid>
  </UtilWrapperLayout>
</template>

<script setup>
import { defu } from 'defu';

const props = defineProps({
    filterSettings: {
        type: Array,
        required: true,
    },

    availableDropdowns: {
        type: Array,
        default: () => [],
    },

    hasTextSearch: {
        type: Boolean,
        default: false,
    },

    dropdownsTitle: {
        type: String,
        default: '',
    },

    pinnedArticles: {
        type: Array,
        default: () => [],
    },

    showLoadMore: {
        type: Boolean,
        default: true,
    },

    colorSchema: {
        type: String,
        default: '',
    },
});

const { dayjs } = useDayJs();
const i18n = useI18n();
const { locale: currentLocale, defaultLocale } = useI18n();
const language = currentLocale.value === defaultLocale ? '' : currentLocale.value;
const { query: routeQuery } = useRoute();
const route = useRoute();
const clientDeviceStore = useClientDeviceStore();

/*
    Pinned Items
*/
const mappedPinnedItems = computed(() => {
    if (!isArrayNotEmpty(props.pinnedArticles)) {
        return [];
    }

    const pinnedArticlesComponent = props.pinnedArticles[0];

    if (!pinnedArticlesComponent) {
        return [];
    }

    const articlesToPin = pinnedArticlesComponent.pinned;

    return articlesToPin
        .filter((item) => item && item.article)
        .map((item) => ({
            article: {
                title: item.article.name,
                lead: item.article?.content?.lead,
                cached_url: item.article.default_full_slug,
                date: item.article?.content?.manualPublishedDate || item.article.first_published_at,
                type: item.article.content.type.name,
                department: {
                    name: item.article.content.department.translated_slugs?.find(
                        (slug) => slug.lang === currentLocale.value,
                    )?.name || item.article.content.department.name,
                    color_3: item.article.content.department.content.color_3,
                },
                image: {
                    ...item.article.content.image_video,
                    provider: 'storyblok',
                },
            },
            image_position: item.image_position,
            size: item.size,
        }));
});

/*
    Query
*/
const routeQueryObject = ref(
    buildStoryblokQuery(routeQuery, i18n),
);

const computedRouteQuery = computed(() => route.query);
watch(computedRouteQuery, (newValue) => {
    routeQueryObject.value = buildStoryblokQuery(newValue, i18n);
}, {
    deep: true,
});

const isFiltered = computed(() => {
    const filteredKeys = Object.keys(computedRouteQuery.value).filter((key) => !key.startsWith('_storyblok'));
    return filteredKeys.length > 0;
});

const baseQueryObject = ref(
    isArrayNotEmpty(props.filterSettings)
        ? buildStoryblokQuery(props.filterSettings, i18n)
        : {},
);

const queryObject = computed(
    () => defu(
        baseQueryObject.value,
        routeQueryObject.value,
    ),
);

const currentPage = shallowRef(1);
const pageSize = ref(7);
const loadMoreVisible = shallowRef(false);

const storyblokApi = useStoryblokApi();

const currentPath = computed(() => route.path);
const localePath = useLocalePath();
const isNewsOverview = computed(() => currentPath.value === localePath('/news/') || currentPath.value === localePath('/news'));

const {
    data: news,
    error: newsError,
    status: newsStatus,
    refresh: refreshNews,
} = useLazyAsyncData(
    `news-articles-${currentLocale.value}`,
    () => storyblokApi.get('cdn/stories/', {
        starts_with: 'news/news-article/',
        version: 'published',
        resolve_links: 'link',
        page: 1,
        per_page: currentPage.value > 1
            ? pageSize.value * currentPage.value - (isFiltered.value ? 1 : 0)
            : pageSize.value,
        filter_query: queryObject.value,
        sort_by: 'first_published_at:desc',
        resolve_relations: 'News.department,News.type,News.country',
        language,
    }),
    {
        transform: (res) => {
            const { data, headers } = res;
            const newStories = data.stories.map((story) => {
                // Compute tag for each article
                let teaserTag = '';
                let textColor = '';
                let dateColor = '';
                let colorSchema = '';
                const tagColors = {
                    backgroundColor: '',
                    color: '',
                };

                // Compute tag logic for individual articles
                if (isNewsOverview.value) {
                    if (story.content.isStiftung) {
                        tagColors.backgroundColor = 'var(--c-petrol-8)';
                        tagColors.color = 'var(--c-white)';
                        textColor = 'var(--c-petrol-1)';
                        dateColor = 'var(--c-petrol-4)';
                        teaserTag = getStoryblokTranslation('general', 'stiftung');
                    } else if (story.content.country && story.content.country.name) {
                        tagColors.backgroundColor = story.content.department.content.color_11;
                        tagColors.color = story.content.department.content.color_4;
                        textColor = story.content.department.content.color_1;
                        dateColor = story.content.department.content.color_4;
                        colorSchema = story.content.department.content.color_schema;
                        teaserTag = getStoryblokTranslation('general', 'international');
                    } else if (story.content.department && story.content.department.name) {
                        tagColors.backgroundColor = 'var(--c-petrol-11)';
                        tagColors.color = 'var(--c-petrol-4)';
                        textColor = 'var(--c-petrol-1)';
                        dateColor = 'var(--c-petrol-4)';
                        teaserTag = getStoryblokTranslation('general', 'switzerland');
                    }
                } else if (story.content.isStiftung) {
                    tagColors.backgroundColor = 'var(--c-petrol-8)';
                    tagColors.color = 'var(--c-white)';
                    textColor = 'var(--c-petrol-1)';
                    dateColor = 'var(--c-petrol-4)';
                    teaserTag = getStoryblokTranslation('general', 'stiftung');
                } else if (story.content.country && story.content.country.name) {
                    tagColors.backgroundColor = story.content.department.content.color_11;
                    tagColors.color = story.content.department.content.color_4;
                    textColor = story.content.department.content.color_1;
                    dateColor = story.content.department.content.color_4;
                    colorSchema = story.content.department.content.color_schema;
                    teaserTag = story.content.country.name;
                } else if (story.content.department && story.content.department.name) {
                    tagColors.backgroundColor = story.content.department.content.color_11;
                    tagColors.color = story.content.department.content.color_4;
                    textColor = story.content.department.content.color_1;
                    dateColor = story.content.department.content.color_4;
                    colorSchema = story.content.department.content.color_schema;
                    teaserTag = story.content.department.translated_slugs?.find(
                        (slug) => slug.lang === currentLocale.value,
                    )?.name || story.content.department?.name;
                }

                return {
                    ...story,
                    cached_url: story.default_full_slug,
                    story: {
                        alternates: story.translated_slugs,
                    },
                    teaserTag,
                    tagColors,
                    textColor,
                    dateColor,
                    colorSchema,
                };
            });

            if (newStories.length >= parseInt(headers?.total, 10)) {
                loadMoreVisible.value = false;
            } else {
                loadMoreVisible.value = props.showLoadMore;
            }

            return newStories;
        },
        watch: [
            currentPage,
        ],
    },
);

watch(queryObject, () => {
    currentPage.value = 1;
    refreshNews();
});

/*
    Error handling
*/
watch(() => newsError.value, (newValue) => {
    if (newValue) {
        useSentryError(newValue);
    }
});

/* Load more */
const loadMore = () => {
    currentPage.value += 1;
};
</script>

<style lang="scss" scoped>
.storyblok-news,
.storyblok-news__filters,
.storyblok-news__load-more {
    @include default-content-columns;
}

/* stylelint-disable no-descending-specificity */
.block-news {
    @include fluid('margin-bottom', 30px, 50px);
    @include fluid('margin-top', 30px, 50px);

    display: flex;
    flex-direction: column;
}

.block-news__img {
    display: block;
    height: 300px;
    background-position: center center;
    object-fit: cover;
    transition: all 0.2s ease-out;
}

.block-news-wrapper {
    @include grid-layout(12);

    @include mobile {
        word-break: break-word;
    }
}

.block-news-wrapper::after {
    content: '';
    // grid-column-end: span 3;
}

.block-news__noArticles {
    margin-bottom: 40px;
    color: v-bind(colorText);
    font-family: var(--f-family--regular);
    font-size: var(--f-size--richtext);
    line-height: var(--l-height--richtext);
}

/* stylelint-disable max-nesting-depth */
.block-news__teaser:first-of-type {

    .is-filtered & {
        position: relative;
        margin-bottom: 45px;
        grid-column-end: span 12;

        &:hover {
            .block-news__img {
                transform: scale(1.05);
                transition: all 0.2s ease-out;
            }
            .block_teaser_video__tag-icon {
                transform: none;
            }
        }

        &.block-news--mini {
            display: flex;
            width: 100%;
            flex-direction: row;

            .block-news__img {
                // width: 400px;
                height: 100%;
                // height: 320px;

                @include mobile {
                    width: 100%;
                }
            }

            .block-news__wrapper {
                display: flex;
                flex-basis: 55%;
                margin-left: 52px;

                .block-news__title-large {
                    display: block;
                    margin-top: 15px;
                    color: v-bind(colorText);
                    font-family: var(--f-family--bold);
                    font-size: var(--f-size--h2);
                    line-height: var(--l-height--h2);
                }

                .block-news__title {
                    display: none;
                }

                .block-news__teaser-large {
                    display: block;
                    margin-bottom: 20px;

                }

                @include tablet-portrait {
                    margin-left: 0;
                }
            }

            @include tablet-portrait {
                flex-direction: column;
            }
        }
    }

}

.block-news__teaser {
    position: relative;
    display: flex;
    margin-bottom: 45px;
    grid-column: auto / span 4;

    @include tablet {
        grid-column: auto / span 6;
    }

    @include mobile {
        grid-column: auto / span 12;
    }

    &:nth-child(7n-5),  &:nth-child(7n-1) {
        flex-direction: column;
        grid-column: auto / span 8;

        .block_teaser_content__wrapper {
            display: flex;
            flex-direction: column;

            .block_teaser__title {
                margin-top: 15px;
                font-family: var(--f-family--bold);
                font-size: var(--f-size--h4);
                line-height: var(--l-height--h4);
            }
        }

        .block_teaser__link {
            padding-top: 20px;
        }

        @include tablet {
            grid-column: auto / span 6;
        }

        @include mobile {
            grid-column: auto / span 12;
        }
    }

    .is-filtered & {
        grid-column-end: span 4;

        @include tablet {
            grid-column-end: span 6;
        }

        @include mobile {
            grid-column-end: span 12;
        }
    }

    &:hover {
        .block-news__img {
            transform: scale(1.05);
        }
        .block_teaser_video__tag-icon {
            transform: none;
        }
        .block-news__icon {
            transform: translateX(10px);
        }
    }

    &.block-news--mini {
        width: 100%;
        flex-direction: column;

        .block-news__img {
            @include mobile {
                width: 100%;
            }
        }

        .block-news__wrapper {
            display: flex;
            flex-direction: column;

            .block-news__date {
                margin-top: 10.87px;
            }

            .block-news__title {
                @include line-clamp(2);

                display: -webkit-box;
                overflow: hidden;
                margin-top: 10px;
                margin-bottom: 20px;
                -webkit-box-orient: vertical;
                color: v-bind(colorText);
                font-family: var(--f-family--bold);
                font-size: var(--f-size--h4);
                line-height: var(--l-height--h4);
                text-overflow: ellipsis;
            }

            .block-news__title-large {
                display: none;
            }

        }

    }

}

.block-news__imgWrapper {
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 320px;

    .is-first & {
        .is-filtered & {
            width: unset;
            height: 400px;
            aspect-ratio: 1/1;

            @include tablet {
                width: 100%;
                height: auto;
                height: 400px;
                aspect-ratio: unset;
            }
        }
    }

    .block-news__tag {
        position: absolute;
        right: 0;
        bottom: 0;
        display: flex;
        height: 48px;
        align-items: center;
        justify-content: center;
        padding: 0 15px;
        color: var(--c-white);
        font-family: var(--f-family--bold);
        font-size: var(--f-size--tag);
        line-height: var(--l-height--tag);
    }

    .block-news__video-tag {
        position: absolute;
        bottom: 0;
        left: 0;
        display: flex;
        width: 92px;
        height: 48px;
        align-items: center;
        justify-content: center;
        background: rgb(182,182,182, 0.9);
        color: var(--c-white);
        font-family: var(--f-family--bold);
        font-size: var(--f-size--tag);
        gap: 5px;
        line-height: var(--l-height--tag);

        span {
            padding-top: 2px;
        }
    }
}

.block-news__date {
    margin-top: 20px;
    font-family: var(--f-family--regular);
    font-size: var(--f-size--richtext);
    line-height: var(--l-height--richtext);
    word-wrap: unset;

}

.block-news_article {
    color: v-bind(colorText);
    font-family: var(--f-family--bold);
    font-size: var(--f-size--richtext);
}

.block-news__icon {
    color: var(--c-primary);
    font-size: 14px;
    transition: all 0.2s ease-out;

    @include tablet-portrait {
        position: relative;
    }
}

.block-news__more-button {
    max-width: 240px;
    align-self: start;
    margin-bottom: 50px;

    &.isHidden {
        display: none;
    }
}

.block-news__lead {

    overflow: hidden;
    margin-bottom: 20px;
    color: v-bind(colorText);
    font-family: var(--f-family--regular);
    font-size: var(--f-size--richtext);
    line-height: var(--l-height--richtext);

    p {
        @include line-clamp(3);

        text-overflow: ellipsis;
    }
}

.storyblok-news__pinned-items {
    @include default-content-columns;
}
</style>
